/**
 * ThemeSelector - Theme-Aware Glass Design System
 *
 * A navbar dropdown component for switching between theme variants.
 * Features color swatches, current theme indicator, and background toggle.
 * Uses CSS variables for theme-aware styling.
 */

import * as React from 'react'
import { cn } from '@/lib/utils'
import { Palette, Check, Image, Settings } from 'lucide-react'
import { useTheme, themeVariants, type ThemeVariant } from '@/contexts/ThemeContext'
import { BackgroundImageManager } from './BackgroundImageManager'

export interface ThemeSelectorProps {
  /** Additional className for the wrapper */
  className?: string
  /** Compact mode - icon only trigger */
  compact?: boolean
}

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ className, compact = true }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isBgManagerOpen, setIsBgManagerOpen] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const { variant, setVariant, showBackgroundImage, setShowBackgroundImage } = useTheme()

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const currentTheme = themeVariants[variant]

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-[var(--theme-radius-md)] transition-all duration-200',
          'bg-[var(--theme-glass-bg)] border border-[var(--theme-glass-border)]',
          'hover:bg-[var(--theme-glass-bg-heavy)] hover:border-[var(--theme-primary)]/30',
          'focus:outline-none focus:ring-2 focus:ring-[var(--theme-focus-ring)]',
          isOpen && 'bg-[var(--theme-glass-bg-heavy)] border-[var(--theme-primary)]/30'
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Select theme"
      >
        {/* Color Swatch Preview */}
        <div
          className="w-5 h-5 rounded-full border-2 border-white/20 shadow-[var(--theme-glow-soft)]"
          style={{ backgroundColor: currentTheme.primaryColor }}
        />
        {!compact && (
          <span className="text-sm font-medium text-[var(--theme-text-secondary)]">
            {currentTheme.name}
          </span>
        )}
        <Palette className="w-4 h-4 text-[var(--theme-text-tertiary)]" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute right-0 top-full mt-2 z-50 min-w-[220px]',
            'bg-[var(--theme-glass-bg-heavy)] backdrop-blur-[24px]',
            'border border-[var(--theme-glass-border-strong)]',
            'rounded-[var(--theme-radius-lg)] shadow-[var(--theme-glow-medium)]',
            'animate-in fade-in-0 zoom-in-95 duration-200'
          )}
        >
          {/* Theme Options */}
          <div className="p-2">
            <div className="px-2 py-1.5 text-xs font-semibold text-[var(--theme-text-tertiary)] uppercase tracking-wider">
              Theme
            </div>
            {(Object.entries(themeVariants) as [ThemeVariant, typeof themeVariants[ThemeVariant]][]).map(
              ([key, theme]) => (
                <ThemeOption
                  key={key}
                  variant={key}
                  theme={theme}
                  isSelected={variant === key}
                  onSelect={() => {
                    setVariant(key)
                    // Don't close dropdown to allow toggling background too
                  }}
                />
              )
            )}
          </div>

          {/* Separator */}
          <div className="mx-2 h-px bg-[var(--theme-glass-border)]" />

          {/* Background Image Toggle */}
          <div className="p-2">
            <button
              onClick={() => setShowBackgroundImage(!showBackgroundImage)}
              className={cn(
                'w-full flex items-center justify-between px-3 py-2.5',
                'rounded-[var(--theme-radius-sm)] transition-all duration-200',
                'hover:bg-[var(--theme-glass-bg)]'
              )}
            >
              <div className="flex items-center gap-3">
                <Image className="w-4 h-4 text-[var(--theme-text-tertiary)]" />
                <span className="text-sm font-medium text-[var(--theme-text-secondary)]">
                  Background Image
                </span>
              </div>
              {/* Toggle Switch */}
              <div
                className={cn(
                  'relative w-9 h-5 rounded-full transition-colors duration-200',
                  showBackgroundImage
                    ? 'bg-[var(--theme-primary)]'
                    : 'bg-[var(--theme-glass-border-strong)]'
                )}
              >
                <div
                  className={cn(
                    'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-200',
                    showBackgroundImage ? 'translate-x-4' : 'translate-x-0.5'
                  )}
                />
              </div>
            </button>

            {/* Manage Background Images Button */}
            <button
              onClick={() => {
                setIsOpen(false)
                setIsBgManagerOpen(true)
              }}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 mt-1',
                'rounded-[var(--theme-radius-sm)] transition-all duration-200',
                'hover:bg-[var(--theme-glass-bg)] text-[var(--theme-text-secondary)]'
              )}
            >
              <Settings className="w-4 h-4 text-[var(--theme-text-tertiary)]" />
              <span className="text-sm font-medium">
                Manage Backgrounds...
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Background Image Manager Modal */}
      <BackgroundImageManager
        isOpen={isBgManagerOpen}
        onClose={() => setIsBgManagerOpen(false)}
      />
    </div>
  )
}

interface ThemeOptionProps {
  variant: ThemeVariant
  theme: (typeof themeVariants)[ThemeVariant]
  isSelected: boolean
  onSelect: () => void
}

const ThemeOption: React.FC<ThemeOptionProps> = ({
  variant,
  theme,
  isSelected,
  onSelect,
}) => {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5',
        'rounded-[var(--theme-radius-sm)] transition-all duration-200',
        isSelected
          ? 'bg-[var(--theme-primary)]/10 text-[var(--theme-primary)]'
          : 'hover:bg-[var(--theme-glass-bg)] text-[var(--theme-text-secondary)]'
      )}
    >
      {/* Color Swatch(es) */}
      <div className="flex -space-x-1">
        <div
          className="w-5 h-5 rounded-full border-2 border-white/20 shadow-sm"
          style={{ backgroundColor: theme.primaryColor }}
        />
        {theme.secondaryColor && (
          <div
            className="w-5 h-5 rounded-full border-2 border-white/20 shadow-sm"
            style={{ backgroundColor: theme.secondaryColor }}
          />
        )}
      </div>

      {/* Theme Info */}
      <div className="flex-1 text-left">
        <div className="text-sm font-medium">{theme.name}</div>
        <div className="text-xs text-[var(--theme-text-tertiary)]">{theme.description}</div>
      </div>

      {/* Selected Indicator */}
      {isSelected && (
        <Check className="w-4 h-4 text-[var(--theme-primary)]" />
      )}
    </button>
  )
}

ThemeSelector.displayName = 'ThemeSelector'

export { ThemeSelector }
